// Control Systems — System Overview & Block Diagrams
// Matches the style/structure of the Magnetism reference page

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Basic Open-Loop vs Closed-Loop ─────────────────────
function DiagramOpenVsClosed() {
  const W = 900, H = 360;

  // Arrow helper
  const Arrow = ({ x1, y1, x2, y2, color='#333', id }) => {
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.sqrt(dx*dx + dy*dy);
    const ux = dx/len, uy = dy/len;
    const hx = x2 - ux*10, hy = y2 - uy*10;
    return (
      <g>
        <line x1={x1} y1={y1} x2={hx} y2={hy} stroke={color} strokeWidth="2"/>
        <polygon points={`${x2},${y2} ${hx-uy*5},${hy+ux*5} ${hx+uy*5},${hy-ux*5}`} fill={color}/>
      </g>
    );
  };

  const Box = ({ x, y, w, h, fill, stroke='#c0392b', label, sub, fontSize=12 }) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={4} fill={fill} stroke={stroke} strokeWidth="1.5"/>
      <text x={x+w/2} y={y+h/2 + (sub?-6:4)} textAnchor="middle" fontSize={fontSize} fill="#1a1a2e" fontWeight="700">{label}</text>
      {sub && <text x={x+w/2} y={y+h/2+12} textAnchor="middle" fontSize={10} fill="#555">{sub}</text>}
    </g>
  );

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Open-Loop vs Closed-Loop Control Systems</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="ah" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0, 7 3, 0 6" fill="#555"/>
          </marker>
          <marker id="ahR" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0, 7 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="ahB" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0, 7 3, 0 6" fill="#2E75B6"/>
          </marker>
          <marker id="ahG" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0, 7 3, 0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* ── TOP PANEL: Open-Loop ── */}
        <rect x={10} y={6} width={W-20} height={148} rx={6} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={W/2} y={24} textAnchor="middle" fontSize={14} fill="#7B0D0D" fontWeight="700">OPEN-LOOP CONTROL SYSTEM</text>

        {/* Boxes */}
        <Box x={30}  y={40} w={110} h={46} fill="#FEF0F0" label="Reference" sub="(Desired Output)" fontSize={11}/>
        <Box x={200} y={40} w={110} h={46} fill="#fff5e6" stroke="#e07b00" label="Controller" sub="(e.g. timer)" fontSize={11}/>
        <Box x={370} y={40} w={110} h={46} fill="#eaf3ff" stroke="#2E75B6" label="Actuator" sub="(e.g. motor)" fontSize={11}/>
        <Box x={540} y={40} w={110} h={46} fill="#f0fff4" stroke="#27ae60" label="Plant / Process" sub="" fontSize={11}/>
        <Box x={710} y={40} w={110} h={46} fill="#f5d0ff" stroke="#9b59b6" label="Output" sub="(Actual)" fontSize={11}/>

        {/* Arrows */}
        <line x1={140} y1={63} x2={196} y2={63} stroke="#555" strokeWidth="2" markerEnd="url(#ah)"/>
        <line x1={310} y1={63} x2={366} y2={63} stroke="#555" strokeWidth="2" markerEnd="url(#ah)"/>
        <line x1={480} y1={63} x2={536} y2={63} stroke="#555" strokeWidth="2" markerEnd="url(#ah)"/>
        <line x1={650} y1={63} x2={706} y2={63} stroke="#555" strokeWidth="2" markerEnd="url(#ah)"/>

        {/* No feedback label */}
        <text x={W/2} y={108} textAnchor="middle" fontSize={12} fill="#c0392b" fontStyle="italic">No feedback path — errors are not corrected automatically</text>
        <line x1={200} y1={118} x2={700} y2={118} stroke="#c0392b" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.5"/>
        <text x={440} y={133} textAnchor="middle" fontSize={10} fill="#c0392b" opacity="0.7">← disturbances accumulate uncorrected →</text>

        {/* ── BOTTOM PANEL: Closed-Loop ── */}
        <rect x={10} y={162} width={W-20} height={192} rx={6} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={W/2} y={180} textAnchor="middle" fontSize={14} fill="#7B0D0D" fontWeight="700">CLOSED-LOOP (FEEDBACK) CONTROL SYSTEM</text>

        {/* Summing junction */}
        <circle cx={100} cy={245} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={100} y={250} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={82}  y={241} textAnchor="middle" fontSize={10} fill="#27ae60" fontWeight="700">+</text>
        <text x={96}  y={262} textAnchor="middle" fontSize={10} fill="#c0392b" fontWeight="700">−</text>

        <Box x={155} y={220} w={110} h={50} fill="#fff5e6" stroke="#e07b00" label="Controller" sub="(C(s))" fontSize={11}/>
        <Box x={320} y={220} w={110} h={50} fill="#eaf3ff" stroke="#2E75B6" label="Plant" sub="(G(s))" fontSize={11}/>
        <Box x={490} y={220} w={110} h={50} fill="#f0fff4" stroke="#27ae60" label="Output" sub="Y(s)" fontSize={11}/>

        {/* Sensor/feedback box */}
        <Box x={320} y={286} w={110} h={46} fill="#f5f0ff" stroke="#9b59b6" label="Sensor" sub="H(s)" fontSize={11}/>

        {/* Forward path arrows */}
        <line x1={30}  y1={245} x2={78}  y2={245} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ahR)"/>
        <text x={20}   y={234} fontSize={10} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={118} y1={245} x2={151} y2={245} stroke="#e07b00" strokeWidth="2" markerEnd="url(#ah)"/>
        <text x={124}  y={234} fontSize={10} fill="#555">E(s)</text>
        <line x1={265} y1={245} x2={316} y2={245} stroke="#555" strokeWidth="2" markerEnd="url(#ah)"/>
        <text x={273}  y={234} fontSize={10} fill="#555">U(s)</text>
        <line x1={430} y1={245} x2={486} y2={245} stroke="#555" strokeWidth="2" markerEnd="url(#ah)"/>
        <line x1={600} y1={245} x2={640} y2={245} stroke="#27ae60" strokeWidth="2"/>
        <text x={618}  y={234} fontSize={10} fill="#27ae60" fontWeight="700">Y(s)</text>

        {/* Feedback path: down from output, across to sensor, back to summing */}
        <line x1={640} y1={245} x2={640} y2={309} stroke="#2E75B6" strokeWidth="2"/>
        <line x1={640} y1={309} x2={434} y2={309} stroke="#2E75B6" strokeWidth="2" markerEnd="url(#ahB)"/>
        <line x1={320} y1={309} x2={200} y2={309} stroke="#2E75B6" strokeWidth="2"/>
        <line x1={200} y1={309} x2={100} y2={309} stroke="#2E75B6" strokeWidth="2"/>
        <line x1={100} y1={309} x2={100} y2={266} stroke="#2E75B6" strokeWidth="2" markerEnd="url(#ahB)"/>
        <text x={370}  y={344} textAnchor="middle" fontSize={10} fill="#2E75B6" fontStyle="italic">Feedback signal B(s) = H(s)·Y(s)</text>

        {/* End arrow */}
        <line x1={640} y1={245} x2={876} y2={245} stroke="#27ae60" strokeWidth="2" markerEnd="url(#ahG)"/>
        <text x={700}  y={234} fontSize={10} fill="#27ae60">Output</text>
      </svg>
      <div style={S.dCaption}>Open-loop systems apply a fixed control action regardless of the actual output. Closed-loop (feedback) systems continuously compare the output to the reference, compute an error signal E(s) = R(s) − B(s), and use that error to drive the controller — enabling automatic correction of disturbances and parameter changes.</div>
    </div>
  );
}

// ── Diagram 2: Canonical Block Diagram with All Elements ──────────
function DiagramCanonical() {
  const W = 980, H = 380;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Canonical Closed-Loop Block Diagram — Labelled Elements</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="a1" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#333"/></marker>
          <marker id="a2" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#c0392b"/></marker>
          <marker id="a3" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#2E75B6"/></marker>
          <marker id="a4" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#27ae60"/></marker>
          <marker id="a5" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#e07b00"/></marker>
        </defs>

        {/* ─── Legend panel ─── */}
        <rect x={755} y={10} width={215} height={200} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={862} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Block Diagram Elements</text>
        {[
          { y:42, fill:"#eaf3ff", stroke:"#2E75B6", label:"Transfer-function block" },
          { y:78, fill:"#FEF0F0", stroke:"#c0392b", label:"Summing junction (Σ)" },
          { y:114,fill:"#f0fff4", stroke:"#27ae60", label:"Pick-off / branch point" },
          { y:150,fill:"#fff5e6", stroke:"#e07b00", label:"Signal (arrow / line)" },
          { y:186,fill:"#f5d0ff", stroke:"#9b59b6", label:"Disturbance input" },
        ].map(({y,fill,stroke,label},i) => (
          <g key={i}>
            <rect x={765} y={y} width={40} height={30} rx={3} fill={fill} stroke={stroke} strokeWidth="1.5"/>
            <text x={815} y={y+19} fontSize={11} fill="#333">{label}</text>
          </g>
        ))}

        {/* ─── Main diagram ─── */}
        {/* Reference input */}
        <text x={15} y={155} fontSize={12} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={40} y1={152} x2={76} y2={152} stroke="#c0392b" strokeWidth="2" markerEnd="url(#a2)"/>

        {/* Summing junction 1 */}
        <circle cx={95} cy={152} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={95} y={157} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={78}  y={148} fontSize={11} fill="#27ae60" fontWeight="700">+</text>
        <text x={91}  y={170} fontSize={11} fill="#c0392b" fontWeight="700">−</text>

        {/* E(s) label */}
        <line x1={113} y1={152} x2={150} y2={152} stroke="#333" strokeWidth="2" markerEnd="url(#a1)"/>
        <text x={118} y={145} fontSize={11} fill="#555">E(s)</text>

        {/* Controller block */}
        <rect x={150} y={127} width={110} height={50} rx={4} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={205} y={150} textAnchor="middle" fontSize={13} fill="#1a1a2e" fontWeight="700">Controller</text>
        <text x={205} y={166} textAnchor="middle" fontSize={11} fill="#e07b00">C(s)</text>

        {/* U(s) */}
        <line x1={260} y1={152} x2={296} y2={152} stroke="#333" strokeWidth="2" markerEnd="url(#a1)"/>
        <text x={265} y={145} fontSize={11} fill="#555">U(s)</text>

        {/* Disturbance summing junction */}
        <circle cx={315} cy={152} r={18} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="2"/>
        <text x={315} y={157} textAnchor="middle" fontSize={16} fill="#9b59b6" fontWeight="700">Σ</text>
        <text x={298} y={148} fontSize={11} fill="#27ae60" fontWeight="700">+</text>
        <text x={311} y={170} fontSize={11} fill="#9b59b6" fontWeight="700">+</text>

        {/* Disturbance input D(s) — from top */}
        <line x1={315} y1={80} x2={315} y2={130} stroke="#9b59b6" strokeWidth="2" markerEnd="url(#a5)" strokeDasharray="5,3"/>
        <text x={322} y={75} fontSize={11} fill="#9b59b6" fontStyle="italic">D(s)</text>
        <text x={330} y={91} fontSize={10} fill="#9b59b6">Disturbance</text>

        {/* Arrow after disturbance junction */}
        <line x1={333} y1={152} x2={368} y2={152} stroke="#333" strokeWidth="2" markerEnd="url(#a1)"/>

        {/* Plant block */}
        <rect x={368} y={127} width={110} height={50} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={423} y={150} textAnchor="middle" fontSize={13} fill="#1a1a2e" fontWeight="700">Plant</text>
        <text x={423} y={166} textAnchor="middle" fontSize={11} fill="#2E75B6">G(s)</text>

        {/* Output line — to pick-off point */}
        <line x1={478} y1={152} x2={556} y2={152} stroke="#27ae60" strokeWidth="2" markerEnd="url(#a4)"/>
        <text x={485} y={145} fontSize={11} fill="#555">Y(s)</text>

        {/* Pick-off point */}
        <circle cx={557} cy={152} r={5} fill="#27ae60"/>
        <line x1={562} y1={152} x2={660} y2={152} stroke="#27ae60" strokeWidth="2" markerEnd="url(#a4)"/>
        <text x={590} y={145} fontSize={12} fill="#27ae60" fontWeight="700">Output Y(s)</text>

        {/* Pick-off down to sensor */}
        <line x1={557} y1={157} x2={557} y2={250} stroke="#2E75B6" strokeWidth="2"/>

        {/* Sensor block */}
        <rect x={422} y={225} width={110} height={50} rx={4} fill="#f5f0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={477} y={248} textAnchor="middle" fontSize={13} fill="#1a1a2e" fontWeight="700">Sensor</text>
        <text x={477} y={264} textAnchor="middle" fontSize={11} fill="#9b59b6">H(s)</text>

        {/* Sensor connection */}
        <line x1={557} y1={250} x2={534} y2={250} stroke="#2E75B6" strokeWidth="2" markerEnd="url(#a3)"/>

        {/* Feedback B(s) back to summing jxn */}
        <line x1={422} y1={250} x2={95} y2={250} stroke="#2E75B6" strokeWidth="2"/>
        <line x1={95}  y1={250} x2={95} y2={173} stroke="#2E75B6" strokeWidth="2" markerEnd="url(#a3)"/>
        <text x={260} y={268} textAnchor="middle" fontSize={11} fill="#2E75B6" fontStyle="italic">Feedback B(s) = H(s) · Y(s)</text>

        {/* Noise input to sensor — from bottom */}
        <line x1={477} y1={310} x2={477} y2={278} stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#a2)" strokeDasharray="4,3"/>
        <text x={484} y={323} fontSize={10} fill="#c0392b" fontStyle="italic">N(s) noise</text>

        {/* Brace labels */}
        <rect x={100} y={340} width={540} height={28} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        <text x={370} y={359} textAnchor="middle" fontSize={11} fill="#7B0D0D">Forward path transfer function: G_OL(s) = C(s)·G(s)   |   Closed-loop: T(s) = C(s)G(s) / [1 + C(s)G(s)H(s)]</text>
      </svg>
      <div style={S.dCaption}>The canonical feedback block diagram contains: a reference R(s), error summing junction, controller C(s), plant G(s), output Y(s), sensor H(s), and optional disturbance D(s) and noise N(s) inputs. The closed-loop transfer function is derived using Mason's gain formula or algebraic reduction.</div>
    </div>
  );
}

// ── Diagram 3: Block Diagram Algebra (Series, Parallel, Feedback) ─
function DiagramAlgebra() {
  const W = 900, H = 420;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Block Diagram Algebra — Reduction Rules</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="ba" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#333"/></marker>
          <marker id="baR" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#c0392b"/></marker>
          <marker id="baB" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#2E75B6"/></marker>
        </defs>

        {/* ─── Row 1: Series ─── */}
        <text x={10} y={28} fontSize={13} fill="#7B0D0D" fontWeight="700">① Series (Cascade):</text>

        {/* G1 */}
        <line x1={10} y1={58} x2={70} y2={58} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>
        <rect x={70} y={38} width={90} height={40} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={115} y={62} textAnchor="middle" fontSize={13} fill="#1a1a2e" fontWeight="700">G₁(s)</text>
        <line x1={160} y1={58} x2={220} y2={58} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>
        <rect x={220} y={38} width={90} height={40} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={265} y={62} textAnchor="middle" fontSize={13} fill="#1a1a2e" fontWeight="700">G₂(s)</text>
        <line x1={310} y1={58} x2={370} y2={58} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>

        <text x={385} y={62} fontSize={18} fill="#c0392b" fontWeight="700">≡</text>

        <line x1={410} y1={58} x2={460} y2={58} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>
        <rect x={460} y={38} width={130} height={40} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={525} y={62} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">G₁(s)·G₂(s)</text>
        <line x1={590} y1={58} x2={650} y2={58} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>

        <rect x={660} y={36} width={230} height={44} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        <text x={670} y={55} fontSize={11} fill="#7B0D0D" fontWeight="700">Equivalent single block:</text>
        <text x={670} y={72} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">G(s) = G₁(s) × G₂(s)</text>

        {/* ─── Row 2: Parallel ─── */}
        <text x={10} y={110} fontSize={13} fill="#7B0D0D" fontWeight="700">② Parallel:</text>

        {/* Input split */}
        <line x1={10} y1={155} x2={70} y2={155} stroke="#333" strokeWidth="2"/>
        <circle cx={70} cy={155} r={4} fill="#333"/>
        <line x1={70} y1={155} x2={70} y2={133} stroke="#333" strokeWidth="2"/>
        <line x1={70} y1={133} x2={120} y2={133} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>
        <line x1={70} y1={155} x2={70} y2={177} stroke="#333" strokeWidth="2"/>
        <line x1={70} y1={177} x2={120} y2={177} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>

        <rect x={120} y={113} width={90} height={40} rx={4} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={165} y={137} textAnchor="middle" fontSize={13} fill="#1a1a2e" fontWeight="700">G₁(s)</text>
        <rect x={120} y={157} width={90} height={40} rx={4} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={165} y={181} textAnchor="middle" fontSize={13} fill="#1a1a2e" fontWeight="700">G₂(s)</text>

        <line x1={210} y1={133} x2={260} y2={133} stroke="#333" strokeWidth="2"/>
        <line x1={210} y1={177} x2={260} y2={177} stroke="#333" strokeWidth="2"/>

        {/* Summing junction */}
        <circle cx={278} cy={155} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={278} y={160} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <line x1={260} y1={133} x2={262} y2={143} stroke="#333" strokeWidth="2"/>
        <line x1={260} y1={177} x2={262} y2={167} stroke="#333" strokeWidth="2"/>
        <line x1={296} y1={155} x2={370} y2={155} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>

        <text x={385} y={159} fontSize={18} fill="#c0392b" fontWeight="700">≡</text>

        <line x1={410} y1={155} x2={460} y2={155} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>
        <rect x={460} y={135} width={130} height={40} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={525} y={159} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">G₁(s)+G₂(s)</text>
        <line x1={590} y1={155} x2={650} y2={155} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>

        <rect x={660} y={133} width={230} height={44} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        <text x={670} y={152} fontSize={11} fill="#7B0D0D" fontWeight="700">Equivalent single block:</text>
        <text x={670} y={169} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">G(s) = G₁(s) + G₂(s)</text>

        {/* ─── Row 3: Unity Feedback ─── */}
        <text x={10} y={235} fontSize={13} fill="#7B0D0D" fontWeight="700">③ Unity Negative Feedback:</text>

        <line x1={10} y1={280} x2={48} y2={280} stroke="#c0392b" strokeWidth="2" markerEnd="url(#baR)"/>
        <circle cx={66} cy={280} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={66} y={285} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={49} y={276} fontSize={11} fill="#27ae60" fontWeight="700">+</text>
        <text x={62} y={297} fontSize={11} fill="#c0392b" fontWeight="700">−</text>
        <line x1={84} y1={280} x2={124} y2={280} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>
        <rect x={124} y={260} width={90} height={40} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={169} y={284} textAnchor="middle" fontSize={13} fill="#1a1a2e" fontWeight="700">G(s)</text>
        <line x1={214} y1={280} x2={280} y2={280} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>
        <circle cx={256} cy={280} r={4} fill="#333"/>
        <line x1={256} y1={284} x2={256} y2={330} stroke="#333" strokeWidth="2"/>
        <line x1={256} y1={330} x2={66}  y2={330} stroke="#333" strokeWidth="2"/>
        <line x1={66}  y1={330} x2={66}  y2={300} stroke="#333" strokeWidth="2" markerEnd="url(#baB)"/>

        <text x={300} y={284} fontSize={18} fill="#c0392b" fontWeight="700">≡</text>

        <line x1={330} y1={280} x2={380} y2={280} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>
        <rect x={380} y={255} width={175} height={50} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={467} y={278} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">G(s)</text>
        <line x1={440} y1={286} x2={495} y2={286} stroke="#7B0D0D" strokeWidth="1"/>
        <text x={467} y={300} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">1 + G(s)</text>
        <line x1={555} y1={280} x2={620} y2={280} stroke="#333" strokeWidth="2" markerEnd="url(#ba)"/>

        <rect x={630} y={255} width={260} height={55} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        <text x={640} y={273} fontSize={11} fill="#7B0D0D" fontWeight="700">Closed-loop TF (unity feedback):</text>
        <text x={640} y={290} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">T(s) = G(s) / [1 + G(s)]</text>
        <text x={640} y={306} fontSize={10} fill="#555">For H(s)≠1: T(s) = G(s)/[1+G(s)H(s)]</text>

        {/* ─── Row 4: Moving pick-off / summing ─── */}
        <text x={10} y={370} fontSize={13} fill="#7B0D0D" fontWeight="700">④ Moving a Pick-off Point Past a Block:</text>
        <rect x={10} y={380} width={880} height={34} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        <text x={20} y={397} fontSize={11} fill="#555" fontFamily="'Courier New',monospace">Move pick-off FORWARD past G(s):  branch signal ÷ G(s)   |   Move pick-off BACKWARD past G(s):  branch signal × G(s)</text>
        <text x={20} y={410} fontSize={11} fill="#555" fontFamily="'Courier New',monospace">Move summing jxn FORWARD past G(s):  add 1/G(s) in branch   |   Move summing jxn BACKWARD:  add G(s) in branch</text>
      </svg>
      <div style={S.dCaption}>Block diagram algebra allows complex interconnected systems to be reduced to a single equivalent transfer function. The three fundamental configurations are series (product), parallel (sum), and feedback (ratio). Moving summing junctions and pick-off points requires compensating blocks to preserve signal equivalence.</div>
    </div>
  );
}

// ── Diagram 4: Signal Flow Graph (SFG) ───────────────────────────
function DiagramSFG() {
  const W = 900, H = 280;
  const nodes = [
    { id:'x1', x:60,  y:140, label:'x₁\n(Input)' },
    { id:'x2', x:220, y:140, label:'x₂' },
    { id:'x3', x:400, y:140, label:'x₃' },
    { id:'x4', x:580, y:140, label:'x₄' },
    { id:'x5', x:760, y:140, label:'x₅\n(Output)' },
  ];
  const branches = [
    { from:'x1', to:'x2', gain:'a₁₂', cy:140, label_dy:-18, color:'#2E75B6' },
    { from:'x2', to:'x3', gain:'a₂₃', cy:140, label_dy:-18, color:'#2E75B6' },
    { from:'x3', to:'x4', gain:'a₃₄', cy:140, label_dy:-18, color:'#2E75B6' },
    { from:'x4', to:'x5', gain:'a₄₅', cy:140, label_dy:-18, color:'#2E75B6' },
    { from:'x4', to:'x2', gain:'b₄₂', cy:220, label_dy:22, color:'#c0392b', feedback:true },
    { from:'x3', to:'x1', gain:'b₃₁', cy:260, label_dy:22, color:'#e07b00', feedback:true },
  ];

  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Signal Flow Graph (SFG) — Mason's Gain Formula</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="sfg" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#333"/></marker>
          <marker id="sfgR" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#c0392b"/></marker>
          <marker id="sfgO" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#e07b00"/></marker>
        </defs>

        {/* Forward branches */}
        {[
          { x1:80,  y1:140, x2:208, y2:140, gain:'a₁₂', gy:126, color:'#2E75B6', mid:'sfg' },
          { x1:240, y1:140, x2:388, y2:140, gain:'a₂₃', gy:126, color:'#2E75B6', mid:'sfg' },
          { x1:420, y1:140, x2:568, y2:140, gain:'a₃₄', gy:126, color:'#2E75B6', mid:'sfg' },
          { x1:600, y1:140, x2:748, y2:140, gain:'a₄₅', gy:126, color:'#2E75B6', mid:'sfg' },
        ].map((b,i) => (
          <g key={i}>
            <line x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2} stroke={b.color} strokeWidth="2" markerEnd={`url(#${b.mid})`}/>
            <text x={(b.x1+b.x2)/2} y={b.gy} textAnchor="middle" fontSize={12} fill={b.color} fontWeight="700">{b.gain}</text>
          </g>
        ))}

        {/* Feedback arc x4 → x2 */}
        <path d="M 580,155 C 580,220 220,220 220,155" fill="none" stroke="#c0392b" strokeWidth="2" markerEnd="url(#sfgR)"/>
        <text x={400} y={228} textAnchor="middle" fontSize={12} fill="#c0392b" fontWeight="700">b₄₂</text>

        {/* Feedback arc x3 → x1 */}
        <path d="M 400,155 C 400,258 60,258 60,155" fill="none" stroke="#e07b00" strokeWidth="2" markerEnd="url(#sfgO)"/>
        <text x={230} y={270} textAnchor="middle" fontSize={12} fill="#e07b00" fontWeight="700">b₃₁</text>

        {/* Nodes */}
        {nodes.map(n => (
          <g key={n.id}>
            <circle cx={n.x} cy={n.y} r={20} fill="#fff" stroke="#333" strokeWidth="2"/>
            {n.label.split('\n').map((l,i) => (
              <text key={i} x={n.x} y={n.y + (n.label.includes('\n') ? -3+i*14 : 4)} textAnchor="middle" fontSize={11} fill="#1a1a2e" fontWeight="700">{l}</text>
            ))}
          </g>
        ))}

        {/* Legend */}
        <rect x={10} y={10} width={340} height={90} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={18} y={28} fontSize={12} fill="#7B0D0D" fontWeight="700">Mason's Gain Formula:</text>
        <text x={18} y={46} fontSize={12} fill="#333" fontFamily="'Courier New',monospace">T = (Σ Pₖ Δₖ) / Δ</text>
        <text x={18} y={63} fontSize={11} fill="#555">Pₖ = gain of k-th forward path</text>
        <text x={18} y={79} fontSize={11} fill="#555">Δ = 1 − Σ(loop gains) + Σ(non-touching loop products) − ...</text>
        <text x={18} y={94} fontSize={11} fill="#555">Δₖ = Δ with loops touching path k removed</text>
      </svg>
      <div style={S.dCaption}>A Signal Flow Graph represents system equations as a directed graph. Nodes are variables; branches carry gains (transfer functions). Mason's Gain Formula computes the overall transfer function directly from the graph topology, identifying all forward paths and feedback loops — without requiring step-by-step block diagram reduction.</div>
    </div>
  );
}

// ── Diagram 5: Time-Domain System Response ───────────────────────
function DiagramStepResponse() {
  const W = 900, H = 300;
  // Approximate 2nd-order underdamped step response curve
  const zeta = 0.3, wn = 1.0;
  const wd = wn * Math.sqrt(1 - zeta*zeta);
  const pts = [];
  for (let i=0; i<=200; i++) {
    const t = i * 0.05;
    const y = 1 - Math.exp(-zeta*wn*t) * (Math.cos(wd*t) + (zeta/Math.sqrt(1-zeta*zeta))*Math.sin(wd*t));
    pts.push({ t, y });
  }

  // Scale to SVG
  const tMax = 10, yMax = 1.45;
  const mx = (t) => 80 + (t/tMax)*(560);
  const my = (y) => 260 - (y/yMax)*(220);

  const pathD = pts.map((p,i) => `${i===0?'M':'L'}${mx(p.t).toFixed(1)},${my(p.y).toFixed(1)}`).join(' ');

  // Key points
  const peakT = pts.reduce((a,b)=>b.y>a.y?b:a);
  const Mp = ((peakT.y - 1) * 100).toFixed(1);
  const riseIdx = pts.findIndex(p=>p.y>=1.0);
  const settleIdx = pts.findIndex((p,i)=>i>30 && pts.slice(i).every(q=>Math.abs(q.y-1)<=0.02));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Second-Order System Step Response — Transient Performance Metrics</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Grid */}
        {[0.25,0.5,0.75,1.0,1.25].map(yv=>(
          <line key={yv} x1={80} y1={my(yv)} x2={640} y2={my(yv)} stroke="#f0cece" strokeWidth="1"/>
        ))}
        {[2,4,6,8,10].map(tv=>(
          <line key={tv} x1={mx(tv)} y1={40} x2={mx(tv)} y2={260} stroke="#f0cece" strokeWidth="1"/>
        ))}

        {/* Steady-state line */}
        <line x1={80} y1={my(1)} x2={640} y2={my(1)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.8"/>
        <text x={648} y={my(1)+4} fontSize={11} fill="#27ae60" fontWeight="700">y=1</text>

        {/* 2% band */}
        <rect x={80} y={my(1.02)} width={560} height={my(0.98)-my(1.02)} fill="#27ae60" opacity="0.08"/>
        <line x1={80} y1={my(1.02)} x2={640} y2={my(1.02)} stroke="#27ae60" strokeWidth="1" strokeDasharray="3,4" opacity="0.5"/>
        <line x1={80} y1={my(0.98)} x2={640} y2={my(0.98)} stroke="#27ae60" strokeWidth="1" strokeDasharray="3,4" opacity="0.5"/>
        <text x={648} y={my(1.02)+4} fontSize={9} fill="#27ae60">±2%</text>

        {/* Response curve */}
        <path d={pathD} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Peak annotation */}
        <line x1={mx(peakT.t)} y1={my(peakT.y)} x2={mx(peakT.t)} y2={my(1)} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={mx(peakT.t)} cy={my(peakT.y)} r={5} fill="#e07b00"/>
        <text x={mx(peakT.t)+6} y={my(peakT.y)-4} fontSize={11} fill="#e07b00" fontWeight="700">Peak ({Mp}%)</text>
        <line x1={mx(peakT.t)} y1={my(peakT.y)-14} x2={mx(peakT.t)} y2={40} stroke="#e07b00" strokeWidth="1" strokeDasharray="3,4" opacity="0.5"/>
        <text x={mx(peakT.t)} y={34} textAnchor="middle" fontSize={10} fill="#e07b00">Tₚ (peak time)</text>

        {/* Rise time */}
        {riseIdx>0 && <>
          <line x1={mx(pts[riseIdx].t)} y1={my(1)} x2={mx(pts[riseIdx].t)} y2={260} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3"/>
          <text x={mx(pts[riseIdx].t)} y={275} textAnchor="middle" fontSize={10} fill="#2E75B6">Tᵣ (rise time)</text>
        </>}

        {/* Settling time */}
        {settleIdx>0 && <>
          <line x1={mx(pts[settleIdx].t)} y1={40} x2={mx(pts[settleIdx].t)} y2={260} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="4,3"/>
          <text x={mx(pts[settleIdx].t)} y={275} textAnchor="middle" fontSize={10} fill="#9b59b6">Tₛ (settling)</text>
        </>}

        {/* Axes */}
        <line x1={80} y1={260} x2={650} y2={260} stroke="#333" strokeWidth="2"/>
        <line x1={80} y1={40}  x2={80}  y2={260} stroke="#333" strokeWidth="2"/>
        <text x={360} y={292} textAnchor="middle" fontSize={12} fill="#333">Time (t)</text>
        <text x={20} y={155} textAnchor="middle" fontSize={12} fill="#333" transform="rotate(-90,20,155)">y(t)</text>

        {/* Axis ticks */}
        {[2,4,6,8,10].map(tv=>(
          <g key={tv}>
            <line x1={mx(tv)} y1={258} x2={mx(tv)} y2={264} stroke="#333" strokeWidth="1.5"/>
            <text x={mx(tv)} y={274} textAnchor="middle" fontSize={10} fill="#555">{tv}</text>
          </g>
        ))}
        {[0,0.5,1.0,1.25].map(yv=>(
          <g key={yv}>
            <line x1={78} y1={my(yv)} x2={82} y2={my(yv)} stroke="#333" strokeWidth="1.5"/>
            <text x={70} y={my(yv)+4} textAnchor="end" fontSize={10} fill="#555">{yv}</text>
          </g>
        ))}

        {/* Info panel */}
        <rect x={660} y={20} width={232} height={260} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={776} y={40} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">2nd-Order System</text>
        <text x={776} y={56} textAnchor="middle" fontSize={11} fill="#555">ζ = 0.3  (underdamped)</text>
        {[
          {y:75,  fill:"#FEF0F0", bc:"#c0392b", t:["Rise Time Tᵣ","10%→90% of final value","↓ with higher ωₙ"]},
          {y:130, fill:"#fff5e6", bc:"#e07b00", t:["Peak Time Tₚ","π / ωd","time to first peak"]},
          {y:182, fill:"#eaf3ff", bc:"#2E75B6", t:["Overshoot Mₚ","e^(−πζ/√(1−ζ²)) × 100%","decreases as ζ→1"]},
          {y:234, fill:"#f0fff4", bc:"#27ae60", t:["Settling Time Tₛ","≈ 4/(ζωₙ)  (2% criterion)","enters ±2% band finally"]},
        ].map(({y,fill,bc,t}) => (
          <g key={y}>
            <rect x={668} y={y} width={216} height={52} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={676} y={y+16} fontSize={11} fill="#7B0D0D" fontWeight="700">{t[0]}</text>
            {t.slice(1).map((l,i)=><text key={i} x={676} y={y+30+i*14} fontSize={10} fill="#444">{l}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The step response of a standard second-order system (ζ=0.3, underdamped) showing the key transient metrics. The damping ratio ζ controls overshoot: ζ&lt;1 is underdamped (oscillatory), ζ=1 is critically damped (fastest without overshoot), ζ&gt;1 is overdamped (sluggish). The natural frequency ωₙ sets the speed of response.</div>
    </div>
  );
}

// ── Diagram 6: Poles & Zeros in the s-plane ───────────────────────
function DiagramSPlane() {
  const W = 900, H = 320;
  const cx = 260, cy = 160, scale = 60;

  const poles = [
    { re:-1, im:2,  label:"p₁ = −1+2j", color:"#c0392b" },
    { re:-1, im:-2, label:"p₂ = −1−2j", color:"#c0392b" },
    { re:-3, im:0,  label:"p₃ = −3",    color:"#c0392b" },
  ];
  const zeros = [
    { re:-2, im:1,  label:"z₁ = −2+j",  color:"#2E75B6" },
    { re:-2, im:-1, label:"z₂ = −2−j",  color:"#2E75B6" },
  ];

  const px = (re) => cx + re * scale;
  const py = (im) => cy - im * scale;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Pole-Zero Plot in the Complex s-Plane</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Grid */}
        {[-4,-3,-2,-1,0,1,2].map(re=>(
          <line key={re} x1={px(re)} y1={20} x2={px(re)} y2={300} stroke="#f0cece" strokeWidth="1"/>
        ))}
        {[-3,-2,-1,0,1,2,3].map(im=>(
          <line key={im} x1={60} y1={py(im)} x2={520} y2={py(im)} stroke="#f0cece" strokeWidth="1"/>
        ))}

        {/* Stability region shading */}
        <rect x={cx} y={20} width={460-cx} height={280} fill="#c0392b" opacity="0.05"/>
        <text x={cx+40} y={36} fontSize={11} fill="#c0392b" opacity="0.7" fontStyle="italic">Unstable (RHP)</text>
        <rect x={60} y={20} width={cx-60} height={280} fill="#27ae60" opacity="0.05"/>
        <text x={80} y={36} fontSize={11} fill="#27ae60" opacity="0.7" fontStyle="italic">Stable (LHP)</text>

        {/* Axes */}
        <line x1={60} y1={cy} x2={520} y2={cy} stroke="#333" strokeWidth="2"/>
        <line x1={cx} y1={20} x2={cx} y2={300} stroke="#333" strokeWidth="2"/>
        <text x={525} y={cy+4} fontSize={12} fill="#333" fontWeight="700">σ (real)</text>
        <text x={cx+4} y={15} fontSize={12} fill="#333" fontWeight="700">jω (imag)</text>

        {/* Tick marks */}
        {[-4,-3,-2,-1,1,2].map(re=>(
          <g key={re}>
            <line x1={px(re)} y1={cy-4} x2={px(re)} y2={cy+4} stroke="#333" strokeWidth="1.5"/>
            <text x={px(re)} y={cy+16} textAnchor="middle" fontSize={10} fill="#555">{re}</text>
          </g>
        ))}
        {[-3,-2,-1,1,2,3].map(im=>(
          <g key={im}>
            <line x1={cx-4} y1={py(im)} x2={cx+4} y2={py(im)} stroke="#333" strokeWidth="1.5"/>
            <text x={cx-8} y={py(im)+4} textAnchor="end" fontSize={10} fill="#555">{im>0?`j${im}`:`−j${Math.abs(im)}`}</text>
          </g>
        ))}

        {/* Poles */}
        {poles.map((p,i) => (
          <g key={i}>
            <text x={px(p.re)} y={py(p.im)+5} textAnchor="middle" fontSize={22} fill={p.color} fontWeight="700">×</text>
            <text x={px(p.re)+14} y={py(p.im)-8} fontSize={10} fill={p.color}>{p.label}</text>
          </g>
        ))}

        {/* Zeros */}
        {zeros.map((z,i) => (
          <g key={i}>
            <circle cx={px(z.re)} cy={py(z.im)} r={8} fill="none" stroke={z.color} strokeWidth="2.5"/>
            <text x={px(z.re)+14} y={py(z.im)+4} fontSize={10} fill={z.color}>{z.label}</text>
          </g>
        ))}

        {/* jω axis label note */}
        <line x1={cx} y1={20} x2={cx} y2={300} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.4"/>
        <text x={cx} y={310} textAnchor="middle" fontSize={10} fill="#c0392b" opacity="0.7">Stability boundary</text>

        {/* Right panel */}
        <rect x={535} y={10} width={355} height={300} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={712} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Pole Locations & System Behaviour</text>

        {[
          { y:40,  fill:"#f0fff4", bc:"#27ae60", title:"Left Half Plane (LHP) poles", desc:["Real part < 0 → exponential decay", "System is BIBO stable", "Further left = faster decay"] },
          { y:105, fill:"#FEF0F0", bc:"#c0392b", title:"Right Half Plane (RHP) poles", desc:["Real part > 0 → exponential growth", "System is UNSTABLE", "Any RHP pole = instability"] },
          { y:170, fill:"#eaf3ff", bc:"#2E75B6", title:"Imaginary axis poles (jω)", desc:["Real part = 0 → sustained oscillation", "Marginally stable", "Used in oscillator design"] },
          { y:224, fill:"#fff5e6", bc:"#e07b00", title:"Complex conjugate poles", desc:["σ ± jω → damped oscillation", "Overshoot & ringing in step resp.", "ζ = |σ|/|s|,  ωₙ = |s|"] },
          { y:272, fill:"#f5d0ff", bc:"#9b59b6", title:"Zeros (○)", desc:["Values of s where G(s) = 0", "Affect shape but not stability (for SISO)"] },
        ].map(({y,fill,bc,title,desc}) => (
          <g key={y}>
            <rect x={542} y={y} width={341} height={desc.length*18+24} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={550} y={y+16} fontSize={11} fill="#7B0D0D" fontWeight="700">{title}</text>
            {desc.map((d,i) => <text key={i} x={550} y={y+32+i*16} fontSize={10} fill="#444">{d}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The s-plane pole-zero plot is the most powerful single diagram in control systems analysis. A system is stable if and only if all poles lie in the open left half-plane. Complex poles produce oscillatory responses; their distance from the origin equals ωₙ and their angle from the negative real axis determines the damping ratio ζ.</div>
    </div>
  );
}

// ── Diagram 7: PID Controller Block Diagram ───────────────────────
function DiagramPID() {
  const W = 900, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>PID Controller — Internal Structure & Block Diagram</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="pid" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#333"/></marker>
          <marker id="pidR" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#c0392b"/></marker>
          <marker id="pidG" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#27ae60"/></marker>
          <marker id="pidB" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><polygon points="0 0,7 3,0 6" fill="#2E75B6"/></marker>
        </defs>

        {/* Reference → summing jxn */}
        <text x={10} y={154} fontSize={11} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={40} y1={150} x2={78} y2={150} stroke="#c0392b" strokeWidth="2" markerEnd="url(#pidR)"/>
        <circle cx={96} cy={150} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={96} y={155} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={79} y={147} fontSize={11} fill="#27ae60" fontWeight="700">+</text>
        <text x={92} y={168} fontSize={11} fill="#c0392b" fontWeight="700">−</text>

        {/* E(s) to PID split */}
        <line x1={114} y1={150} x2={160} y2={150} stroke="#333" strokeWidth="2"/>
        <circle cx={160} cy={150} r={4} fill="#333"/>
        <text x={128} y={143} fontSize={10} fill="#555">E(s)</text>

        {/* Three parallel PID branches */}
        {/* P branch — middle */}
        <line x1={160} y1={150} x2={200} y2={150} stroke="#333" strokeWidth="2" markerEnd="url(#pid)"/>
        <rect x={200} y={130} width={90} height={40} rx={4} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={245} y={148} textAnchor="middle" fontSize={12} fill="#1a1a2e" fontWeight="700">Kₚ</text>
        <text x={245} y={164} textAnchor="middle" fontSize={10} fill="#e07b00">Proportional</text>
        <line x1={290} y1={150} x2={370} y2={150} stroke="#e07b00" strokeWidth="2" markerEnd="url(#pid)"/>

        {/* I branch — top */}
        <line x1={160} y1={150} x2={160} y2={75} stroke="#333" strokeWidth="2"/>
        <line x1={160} y1={75} x2={200} y2={75} stroke="#333" strokeWidth="2" markerEnd="url(#pid)"/>
        <rect x={200} y={55} width={90} height={40} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={245} y={73} textAnchor="middle" fontSize={12} fill="#1a1a2e" fontWeight="700">Kᵢ/s</text>
        <text x={245} y={89} textAnchor="middle" fontSize={10} fill="#2E75B6">Integral</text>
        <line x1={290} y1={75} x2={370} y2={75} stroke="#2E75B6" strokeWidth="2"/>
        <line x1={370} y1={75} x2={370} y2={148} stroke="#2E75B6" strokeWidth="2" markerEnd="url(#pidB)"/>

        {/* D branch — bottom */}
        <line x1={160} y1={150} x2={160} y2={225} stroke="#333" strokeWidth="2"/>
        <line x1={160} y1={225} x2={200} y2={225} stroke="#333" strokeWidth="2" markerEnd="url(#pid)"/>
        <rect x={200} y={205} width={90} height={40} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={245} y={223} textAnchor="middle" fontSize={12} fill="#1a1a2e" fontWeight="700">Kd·s</text>
        <text x={245} y={239} textAnchor="middle" fontSize={10} fill="#27ae60">Derivative</text>
        <line x1={290} y1={225} x2={370} y2={225} stroke="#27ae60" strokeWidth="2"/>
        <line x1={370} y1={225} x2={370} y2={152} stroke="#27ae60" strokeWidth="2" markerEnd="url(#pidG)"/>

        {/* Summing junction for PID output */}
        <circle cx={388} cy={150} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={388} y={155} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={372} y={147} fontSize={11} fill="#27ae60" fontWeight="700">+</text>

        {/* U(s) → Plant */}
        <line x1={406} y1={150} x2={450} y2={150} stroke="#333" strokeWidth="2" markerEnd="url(#pid)"/>
        <text x={415} y={143} fontSize={10} fill="#555">U(s)</text>
        <rect x={450} y={130} width={90} height={40} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={495} y={148} textAnchor="middle" fontSize={12} fill="#1a1a2e" fontWeight="700">Plant</text>
        <text x={495} y={163} textAnchor="middle" fontSize={10} fill="#2E75B6">G(s)</text>
        <line x1={540} y1={150} x2={610} y2={150} stroke="#333" strokeWidth="2" markerEnd="url(#pid)"/>
        <text x={545} y={143} fontSize={10} fill="#555">Y(s)</text>

        {/* Feedback to summing jxn */}
        <circle cx={590} cy={150} r={4} fill="#333"/>
        <line x1={590} y1={154} x2={590} y2={265} stroke="#333" strokeWidth="2"/>
        <line x1={590} y1={265} x2={96}  y2={265} stroke="#333" strokeWidth="2"/>
        <line x1={96}  y1={265} x2={96}  y2={170} stroke="#333" strokeWidth="2" markerEnd="url(#pid)"/>

        {/* PID formula */}
        <rect x={630} y={100} width={260} height={110} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={760} y={118} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">PID Transfer Function</text>
        <text x={640} y={138} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">C(s) = Kₚ + Kᵢ/s + Kd·s</text>
        <line x1={640} y1={145} x2={880} y2={145} stroke="#f0cece" strokeWidth="1"/>
        <text x={640} y={162} fontSize={11} fill="#555">Kₚ: reduces steady-state error</text>
        <text x={640} y={178} fontSize={11} fill="#555">Kᵢ: eliminates steady-state error</text>
        <text x={640} y={194} fontSize={11} fill="#555">Kd: improves transient response</text>

        <rect x={630} y={220} width={260} height={64} rx={4} fill="#FEF0F0" stroke="#c0392b"/>
        <text x={640} y={236} fontSize={11} fill="#7B0D0D" fontWeight="700">Ziegler–Nichols Tuning:</text>
        <text x={640} y={252} fontSize={10} fill="#555" fontFamily="'Courier New',monospace">1. Set Kᵢ=Kd=0, increase Kₚ to Kᵤ</text>
        <text x={640} y={268} fontSize={10} fill="#555" fontFamily="'Courier New',monospace">2. Record ultimate gain Kᵤ, period Tᵤ</text>
        <text x={640} y={282} fontSize={10} fill="#555" fontFamily="'Courier New',monospace">3. Kₚ=0.6Kᵤ, Kᵢ=1.2Kᵤ/Tᵤ, Kd=0.075KᵤTᵤ</text>
      </svg>
      <div style={S.dCaption}>The PID controller computes three terms in parallel: proportional (error magnitude), integral (accumulated error — eliminates steady-state offset), and derivative (rate of change — improves damping). The three gains Kₚ, Kᵢ, Kd can be tuned by Ziegler–Nichols, pole placement, or numerical optimisation methods.</div>
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────
export default function ControlSystems() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — System Overview &amp; Block Diagrams</h1>
      <p style={S.p}>
        A control system is an arrangement of physical or computational components whose purpose is to command, regulate, or direct another system. Block diagrams are the universal language of control engineering: they represent mathematical relationships between signals using boxes (transfer functions), arrows (signal flow), summing junctions, and pick-off points — providing both a visual map of system structure and a direct route to algebraic analysis.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Every control system has an input (reference), a process (plant), and an output — open-loop or closed-loop",
        "Feedback compares output to reference, forming an error signal that drives the controller",
        "A block represents a transfer function G(s) = Y(s)/U(s) — valid when the system is LTI (linear, time-invariant)",
        "Series blocks multiply; parallel blocks add; a negative-feedback loop gives T(s) = G(s)/[1+G(s)H(s)]",
        "Signal Flow Graphs (SFG) and Mason's Gain Formula provide a systematic reduction algorithm",
        "Stability is determined by pole locations in the s-plane — all poles must be in the left half-plane (LHP)",
        "The PID controller is the most widely deployed controller in industry (~95% of all feedback loops)",
        "Key transient metrics: rise time Tᵣ, peak time Tₚ, percent overshoot Mₚ, settling time Tₛ",
      ]}/>

      <h2 style={S.h2}>Open-Loop vs Closed-Loop Systems</h2>
      <p style={S.p}>
        The most fundamental distinction in control systems is whether the output is measured and fed back to influence the input. Open-loop systems apply a pre-programmed control action without any knowledge of the actual output — simple, cheap, but unable to reject disturbances. Closed-loop (feedback) systems continuously monitor the output and correct any deviation from the desired value.
      </p>
      <DiagramOpenVsClosed/>
      <Table2 rows={[
        ["Open-loop system","No feedback path; output has no influence on input\nExample: a toaster (fixed timer), a washing machine (fixed cycle)\nError accumulates if plant changes or disturbances occur"],
        ["Closed-loop system","Output is measured, fed back, and subtracted from reference\nError signal E(s) = R(s) − B(s) drives the controller\nSelf-correcting — rejects disturbances automatically"],
        ["Reference R(s)","Desired output (setpoint); the 'command' input to the system"],
        ["Error E(s)","E(s) = R(s) − B(s); difference between reference and feedback signal"],
        ["Controller C(s)","Processes the error and generates the control action U(s)"],
        ["Plant G(s)","The physical process being controlled (motor, furnace, aircraft, etc.)"],
        ["Sensor H(s)","Measures the output and converts it to a feedback signal B(s)"],
        ["Disturbance D(s)","Unwanted external input that corrupts the output\nGood controllers minimise disturbance effects"],
      ]}/>

      <h2 style={S.h2}>Block Diagram Elements & Notation</h2>
      <p style={S.p}>
        Block diagrams are built from four primitive elements: transfer function blocks, signal lines (arrows), summing junctions, and pick-off points. Mastering how to read and draw these diagrams is the first step in control system analysis.
      </p>
      <DiagramCanonical/>
      <Table2 rows={[
        ["Transfer function block","A rectangle labelled G(s) representing Y(s) = G(s)·U(s)\nValid only for LTI systems in zero initial-condition (ZIC) form\nDerived via Laplace transform of the system's differential equation"],
        ["Summing junction","A circle with Σ; algebraically adds (or subtracts) input signals\nSigns (+/−) shown at each incoming arrow\nOutput = algebraic sum of all inputs"],
        ["Pick-off point","A filled dot; 'taps' a signal and sends it to one or more branches\nThe signal is not modified — each branch receives the full value"],
        ["Signal line / arrow","Carries a signal (variable) from one block to another\nDirection of arrow = direction of signal causality\nLabelled with the signal name e.g. E(s), U(s), Y(s)"],
        ["Unity feedback","H(s) = 1; sensor gain is 1 (output fed back directly)\nSimplifies closed-loop TF to T(s) = G(s)/[1+G(s)]"],
        ["Non-unity feedback","H(s) ≠ 1; sensor has its own dynamics (e.g. a tachometer)\nClosed-loop TF: T(s) = G(s)/[1+G(s)H(s)]"],
      ]}/>

      <h2 style={S.h2}>Block Diagram Algebra — Reduction Rules</h2>
      <p style={S.p}>
        Any block diagram, however complex, can be reduced to a single transfer function by repeatedly applying three fundamental reduction rules: series combination, parallel combination, and feedback loop elimination. Additional rules for moving summing junctions and pick-off points past blocks allow the diagram to be rearranged before applying the core rules.
      </p>
      <DiagramAlgebra/>
      <Table2 rows={[
        ["Series (cascade)","G(s) = G₁(s) · G₂(s) · … · Gₙ(s)\nOutput of each block is the input of the next\nOrder does not matter for LTI blocks (commutative)"],
        ["Parallel","G(s) = G₁(s) + G₂(s) + … (signs from summing junction)\nBoth blocks receive the same input; outputs are summed"],
        ["Negative feedback loop","T(s) = G(s) / [1 + G(s)H(s)]\nG(s) = forward path, H(s) = feedback path\nDenominator 1+G(s)H(s) is the characteristic polynomial"],
        ["Positive feedback loop","T(s) = G(s) / [1 − G(s)H(s)]\nRarely used intentionally; can cause instability\nFound in some oscillator and regenerative circuits"],
        ["Move pick-off forward past G","Divide the branch signal by G(s) to compensate"],
        ["Move pick-off backward past G","Multiply the branch signal by G(s) to compensate"],
        ["Move summing jxn forward past G","Add a block of 1/G(s) in the moved branch"],
        ["Move summing jxn backward past G","Add a block of G(s) in the moved branch"],
      ]}/>

      <h2 style={S.h2}>Signal Flow Graphs &amp; Mason's Gain Formula</h2>
      <p style={S.p}>
        A Signal Flow Graph (SFG) is an alternative representation of a system of linear equations. Whereas block diagram reduction requires multiple algebraic steps, Mason's Gain Formula gives the transfer function directly from the graph topology in a single calculation, by systematically identifying all forward paths and feedback loops.
      </p>
      <DiagramSFG/>
      <Table2 rows={[
        ["Node","A variable (signal) in the system; represented as a labelled dot\nSource nodes have only outgoing branches; sink nodes only incoming"],
        ["Branch","A directed edge from node xᵢ to xⱼ carrying gain aᵢⱼ (transfer function)\nSignal is multiplied by the branch gain as it passes through"],
        ["Forward path Pₖ","A path from source to sink that does not pass through any node more than once\nGain = product of all branch gains along the path"],
        ["Loop gain Lₘ","A closed path (loop) gain = product of all branch gains in the loop\nA self-loop on a single node is allowed"],
        ["Non-touching loops","Two or more loops that share no common nodes\nProduct of their gains appears in the graph determinant Δ"],
        ["Graph determinant Δ","Δ = 1 − ΣL₁ + ΣL₂L₃(non-touching) − ΣL₄L₅L₆(non-touching) + …\nTerms alternate in sign; higher-order terms rarely contribute"],
        ["Path cofactor Δₖ","Δ with all loops touching the k-th forward path removed\nAlways 1 if no loops are non-touching with the path"],
        ["Mason's formula","T = (Σ Pₖ Δₖ) / Δ\nSums contributions of all forward paths, each weighted by its cofactor"],
      ]}/>

      <h2 style={S.h2}>Transfer Functions &amp; the Laplace Domain</h2>
      <p style={S.p}>
        Block diagrams operate in the Laplace (s-domain) because the Laplace transform converts differential equations into algebraic equations, making it possible to represent any LTI component as a simple ratio of polynomials. The transfer function G(s) = Y(s)/U(s) completely characterises the input-output behaviour of a zero-initial-condition LTI system.
      </p>
      <Table2 rows={[
        ["Laplace transform","L{f(t)} = F(s) = ∫₀^∞ f(t)e^(−st) dt\nConverts time-domain ODEs into algebraic equations in s"],
        ["Transfer function G(s)","G(s) = Y(s)/U(s) = numerator polynomial / denominator polynomial\nOnly valid for LTI systems with zero initial conditions"],
        ["Poles","Values of s where G(s) → ∞ (roots of denominator)\nDetermine stability and natural response modes"],
        ["Zeros","Values of s where G(s) = 0 (roots of numerator)\nShape the frequency response and transient response"],
        ["Order n","Degree of the denominator polynomial\n1st-order: single pole, exponential response\n2nd-order: two poles, possible oscillation"],
        ["DC gain","G(0) = value of G(s) at s=0\nThe steady-state output per unit step input"],
        ["Type number","Number of free integrators (poles at s=0) in the forward path\nType 0: finite position error; Type 1: zero position error; Type 2: zero velocity error"],
        ["Characteristic equation","Denominator of closed-loop TF set to zero: 1+G(s)H(s) = 0\nRoots are the closed-loop poles; determines stability"],
      ]}/>

      <h2 style={S.h2}>Transient Response &amp; Performance Metrics</h2>
      <p style={S.p}>
        The step response of a control system reveals its dynamic behaviour. For a standard second-order system G(s) = ωₙ²/[s² + 2ζωₙs + ωₙ²], the damping ratio ζ and natural frequency ωₙ completely determine all transient metrics.
      </p>
      <DiagramStepResponse/>
      <Table2 rows={[
        ["Damping ratio ζ","Controls the shape of the step response\nζ < 1: underdamped (oscillatory); ζ = 1: critically damped; ζ > 1: overdamped"],
        ["Natural frequency ωₙ","Speed of the natural (unforced) oscillation; units rad/s\nHigher ωₙ → faster response"],
        ["Rise time Tᵣ","Time to go from 10% to 90% of final value\nTᵣ ≈ (1.8)/ωₙ for second-order systems"],
        ["Peak time Tₚ","Time to reach the first peak of overshoot\nTₚ = π/ωd  where ωd = ωₙ√(1−ζ²)"],
        ["Percent overshoot Mₚ","%OS = e^(−πζ/√(1−ζ²)) × 100\n%OS is a function of ζ alone — independent of ωₙ"],
        ["Settling time Tₛ","Time for response to stay within ±2% of final value\nTₛ ≈ 4/(ζωₙ) for 2% criterion"],
        ["Steady-state error eₛₛ","Difference between reference and output as t→∞\nFor unity-feedback Type 0: eₛₛ = 1/(1+Kₚ) for step input"],
        ["Damped natural frequency ωd","ωd = ωₙ√(1−ζ²) — actual frequency of oscillation\nEqual to imaginary part of complex conjugate poles"],
      ]}/>

      <h2 style={S.h2}>Pole-Zero Analysis &amp; Stability</h2>
      <p style={S.p}>
        The location of closed-loop poles in the complex s-plane is the most direct indicator of system behaviour. The Routh-Hurwitz criterion determines stability algebraically; the Root Locus method shows how poles move as a parameter (usually controller gain) varies.
      </p>
      <DiagramSPlane/>
      <Table2 rows={[
        ["Stability condition","A system is stable ⟺ ALL closed-loop poles have negative real parts\n(all poles lie in the open left half-plane)"],
        ["Routh-Hurwitz criterion","Determines stability from coefficients of characteristic polynomial\nNo need to find roots — construct Routh array and check sign changes\nNumber of RHP poles = number of sign changes in first column"],
        ["Root Locus","Graphical method showing how closed-loop poles move as gain K varies (0→∞)\nStarts at open-loop poles (K=0); ends at open-loop zeros (K→∞)\nUsed to choose gain for desired closed-loop pole locations"],
        ["Bode plot","Frequency response: magnitude (dB) and phase (°) vs frequency ω\nGain margin: dB gain at phase = −180° (positive = stable)\nPhase margin: phase above −180° at gain crossover (positive = stable)"],
        ["Nyquist criterion","Contour-based stability test in the frequency domain\nCounts encirclements of (−1,0) to find RHP closed-loop poles\nHandles time delays and non-minimum-phase systems"],
        ["Gain margin Gₘ","Factor by which gain can increase before instability: Gₘ = 1/|G(jω₁₈₀)|\nTypically desired: Gₘ ≥ 6 dB"],
        ["Phase margin φₘ","Additional phase lag to reach instability at gain crossover\nTypically desired: φₘ ≥ 45°\nRelated to damping: φₘ ≈ 100ζ degrees (for ζ < 0.7)"],
      ]}/>

      <h2 style={S.h2}>PID Controllers — The Industry Standard</h2>
      <p style={S.p}>
        The Proportional-Integral-Derivative (PID) controller accounts for an estimated 95% of all feedback control loops in industry. It computes a control signal from three parallel terms acting on the error signal, providing a powerful and intuitive three-parameter tuning knob for achieving the desired closed-loop response.
      </p>
      <DiagramPID/>
      <Table2 rows={[
        ["Proportional (Kₚ)","u(t) ∝ Kₚ · e(t)\nReduces error magnitude; too high → oscillation or instability"],
        ["Integral (Kᵢ)","u(t) ∝ Kᵢ ∫e(t)dt  →  Kᵢ/s in s-domain\nEliminates steady-state error; too high → instability (windup)"],
        ["Derivative (Kd)","u(t) ∝ Kd · de/dt  →  Kd·s in s-domain\nDamps oscillation; sensitive to noise — often filtered in practice"],
        ["Ideal PID TF","C(s) = Kₚ + Kᵢ/s + Kd·s = (Kd·s² + Kₚ·s + Kᵢ) / s\nAdds a zero pair and an integrator to the open-loop"],
        ["Ziegler–Nichols (step)","Use open-loop step response S-curve: L = delay, T = time constant\nKₚ=1.2T/L, Kᵢ=2L, Kd=0.5L"],
        ["Anti-windup","Prevents integrator saturation when actuator is at its limit\nBack-calculation or clamping methods widely used"],
        ["Derivative filter","D term often implemented as Kd·s/(1+s/N); filter N limits noise amp.\nTypical N = 5–20; trade-off between noise rejection and derivative action"],
        ["Setpoint weighting","Apply b·R−Y to P term, c·R−Y to D term (b,c ∈ [0,1])\nReduces overshoot to setpoint changes without affecting disturbance rejection"],
      ]}/>

      <h2 style={S.h2}>System Types &amp; Steady-State Error</h2>
      <Table2 rows={[
        ["System Type","Number of integrators (poles at s=0) in the open-loop transfer function\nType 0, 1, 2, … — higher type → fewer steady-state errors"],
        ["Position error constant Kₚ","Kₚ = lim(s→0) G(s)H(s)\nSteady-state error to step: eₛₛ = 1/(1+Kₚ)  (Type 0)"],
        ["Velocity error constant Kᵥ","Kᵥ = lim(s→0) s·G(s)H(s)\nSteady-state error to ramp: eₛₛ = 1/Kᵥ  (Type 1)"],
        ["Acceleration error constant Kₐ","Kₐ = lim(s→0) s²·G(s)H(s)\nSteady-state error to parabola: eₛₛ = 1/Kₐ  (Type 2)"],
        ["Final Value Theorem","y(∞) = lim(s→0) s·Y(s)\nUsed to compute steady-state output without inverse Laplace transform"],
        ["Error vs system type","Type 0: finite step error, infinite ramp error\nType 1: zero step error, finite ramp error, infinite parabola error\nType 2: zero step error, zero ramp error, finite parabola error"],
      ]}/>

      <h2 style={S.h2}>Common Plant Models &amp; Their Block Representations</h2>
      <Table2 rows={[
        ["1st-order plant","G(s) = K / (τs + 1)   (K=DC gain, τ=time constant)\nStep response: y(t) = K(1 − e^(−t/τ))\nExamples: RC circuit, liquid level tank, simple thermal system"],
        ["2nd-order plant","G(s) = ωₙ² / (s² + 2ζωₙs + ωₙ²)\nStep response depends on ζ; pole pair at s = −ζωₙ ± jωd\nExamples: spring-mass-damper, armature-controlled DC motor"],
        ["Integrating plant","G(s) = K/s — velocity-type process\nOpen-loop ramp output to step input\nExamples: position control (motor position vs current)"],
        ["Dead-time (transport delay)","G(s) = e^(−Ls) · P(s)   L = delay, P(s) = rational part\nPhase lag without magnitude change → destabilising\nExamples: conveyor belts, chemical reactors, network transmission"],
        ["Right-half-plane zero (RHPZ)","Non-minimum phase: initial inverse response before settling\nCommon in boost converters, and some process plants\nLimits achievable bandwidth; complicates controller design"],
        ["State-space representation","ẋ = Ax + Bu,  y = Cx + Du\nA: system matrix; B: input; C: output; D: feedthrough\nTransfer function: G(s) = C(sI−A)⁻¹B + D"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Closed-loop TF (negative feedback): T(s) = G(s) / [1 + G(s)H(s)]",
        "Series blocks: G(s) = G₁(s)·G₂(s)·...·Gₙ(s)",
        "Parallel blocks: G(s) = G₁(s) + G₂(s) + ... (with correct signs)",
        "Mason's Gain Formula: T = (Σ Pₖ Δₖ) / Δ",
        "2nd-order step response: %OS = exp(−πζ/√(1−ζ²))×100,  Tₛ ≈ 4/(ζωₙ)",
        "PID controller: C(s) = Kₚ + Kᵢ/s + Kd·s",
        "Characteristic equation: 1 + G(s)H(s) = 0   (roots = closed-loop poles)",
        "Stability: all closed-loop poles must lie in the open left half-plane (Re[s] < 0)",
      ]}/>
    </div>
  );
}